import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { contactInfo, services } from '../data/mock';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          service: '',
          subject: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Us | SRAGCircle OPC - Get a Proposal or Inquiry"
        description="Contact SRAGCircle OPC for IT systems integration, SCADA automation, security surveillance, and facilities management services. Request a proposal or send us an inquiry."
        keywords="contact SRAGCircle OPC, IT services inquiry Philippines, request proposal Metro Manila, systems integrator contact"
        canonical="/contact"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[#0066CC]/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-[#00C0FF]/10 rounded-full blur-[80px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00C0FF] font-semibold text-sm tracking-wider uppercase">Get In Touch</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Contact <span className="text-[#0066CC]">SRAGCircle</span> OPC
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Talk to us about your IT, security, power, or facilities requirements. Share a few details and our team will get in touch.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-2xl font-bold text-[#1a1a5c] mb-6">Contact Information</h2>

                {/* Contact Details */}
                <div className="space-y-6">
                  <a
                    href={`tel:${contactInfo.mobile}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-colors">
                      <Phone className="w-5 h-5 text-[#0066CC] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mobile</p>
                      <p className="font-medium text-[#1a1a5c] group-hover:text-[#0066CC] transition-colors">{contactInfo.mobile}</p>
                    </div>
                  </a>

                  <a
                    href={`tel:${contactInfo.officePhone}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-colors">
                      <Phone className="w-5 h-5 text-[#0066CC] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Office Phone</p>
                      <p className="font-medium text-[#1a1a5c] group-hover:text-[#0066CC] transition-colors">{contactInfo.officePhone}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-colors">
                      <Mail className="w-5 h-5 text-[#0066CC] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-[#1a1a5c] group-hover:text-[#0066CC] transition-colors break-all">{contactInfo.email}</p>
                    </div>
                  </a>
                </div>

                {/* Alternate Emails */}
                <div className="mt-8 pt-8 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-3">Alternative Email Addresses:</p>
                  <div className="space-y-2">
                    {contactInfo.alternateEmails.map((email, idx) => (
                      <a
                        key={idx}
                        href={`mailto:${email}`}
                        className="block text-sm text-[#0066CC] hover:underline"
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-[#0066CC]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1a1a5c]">Send Us a Message</h2>
                    <p className="text-gray-500 text-sm">We'll respond within 24 hours</p>
                  </div>
                </div>

                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1a1a5c] mb-3">Message Sent!</h3>
                    <p className="text-gray-600">Thank you for contacting us. We'll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#1a1a5c] font-medium">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-[#1a1a5c] font-medium">Company / Organization</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1a1a5c] font-medium">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#1a1a5c] font-medium">Mobile Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+63 XXX XXX XXXX"
                          className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-[#1a1a5c] font-medium">Service Needed</Label>
                        <Select value={formData.service} onValueChange={handleServiceChange}>
                          <SelectTrigger className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service.id} value={service.slug}>
                                {service.title}
                              </SelectItem>
                            ))}
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-[#1a1a5c] font-medium">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                          required
                          className="h-12 border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#1a1a5c] font-medium">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your requirements..."
                        required
                        rows={5}
                        className="border-gray-200 focus:border-[#0066CC] focus:ring-[#0066CC] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D20000] hover:bg-[#B00000] text-white h-14 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
